// Loads every collection the legacy components expect on `window.QH_DATA`
// from the live API in parallel, then notifies subscribers so React re-renders.
// Keeping the shape identical means the original screen code reads the same
// way as before; only mutations need to call `api.post(...)` + `store.reload()`.

const api = window.QH_API;

const listeners = new Set();
function notify() { for (const fn of listeners) fn(); }

function isoToLocal(s) {
  if (!s) return s;
  if (typeof s !== 'string') return s;
  return s.replace('T', ' ').replace(/\.\d+Z?$/, '').substring(0, 19);
}

// Display helpers — exposed on window so every screen can use the same
// rule. We keep STORED dates in ISO ("YYYY-MM-DD[ HH:MM:SS]") because a
// lot of the UI sorts via .localeCompare() and other places do date
// math via `new Date(s)`; both depend on lexicographic == chronological
// order, which only ISO gives. Display is reformatted at the leaf to
// DD-MM-YYYY (and DD-MM-YYYY HH:MM[:SS] when a time is present).
function fmtDate(s) {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(s);
  return `${m[3]}-${m[2]}-${m[1]}`;
}
function fmtDateTime(s) {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return fmtDate(s);
  return `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}` + (m[6] ? `:${m[6]}` : '');
}
window.fmtDate = fmtDate;
window.fmtDateTime = fmtDateTime;

async function loadAll() {
  // Every collection is gated by RBAC and a non-admin role will get 403 on
  // anything they don't have read on. We still bootstrap — the per-page
  // permission check + nav filter hide the rest of the UI — so each fetch
  // gets a `.catch(safe)` that falls back to an empty default. Only
  // /auth/me is allowed to throw, since without it the app has no idea
  // who's logged in.
  const safeArr = () => [];
  const safeObj = () => ({});
  const safePage = () => ({ items: [] });
  const me = await api.get('/auth/me');
  const [schemes, events, txsResp, fin, rep, def, users, roles, pages, auditResp, templates, dash, membersResp, banks, bankTransfers] =
    await Promise.all([
      api.get('/schemes').catch(safeArr),
      api.get('/events').catch(safeArr),
      api.get('/transactions?pageSize=500').catch(safePage),
      api.get('/finance/applications').catch(safeArr),
      api.get('/repayments').catch(safeArr),
      api.get('/finance/defaulters').catch(safeArr),
      api.get('/users').catch(safeArr),
      api.get('/roles').catch(safeArr),
      api.get('/pages').catch(safeArr),
      api.get('/audit?pageSize=500').catch(safePage),
      api.get('/templates').catch(safeArr),
      api.get('/dashboard').catch(safeObj),
      api.get('/members?pageSize=1000').catch(safePage),
      api.get('/banks').catch(safeArr),
      api.get('/banks/transfers').catch(safeArr),
    ]);

  const Q = window.QH_DATA;

  Q.currentUser = {
    id: me.user.id, name: me.user.name, role: me.user.role,
    email: me.user.email, avatarInitials: me.user.avatarInitials,
  };
  Q.myPerms = me.permissions; // flat: {[pageId]: {read,create,...}} — used by can()
  Q.activeRole = { id: me.role.id, name: me.role.name };

  Q.pages = pages.map(p => ({ id: p.id, label: p.label }));
  Q.roles = roles.map(r => ({ id: r.id, name: r.name, desc: r.description, userCount: r.userCount, locked: r.locked }));

  // Build the legacy per-role permission matrix used by the RBAC views.
  // Non-admins typically can't read /roles/{id}/permissions — fall back to
  // an empty array per role rather than blowing up the whole bootstrap.
  const allPerms = await Promise.all(roles.map(r => api.get(`/roles/${r.id}/permissions`).catch(() => [])));
  const matrix = {};
  roles.forEach((r, i) => {
    matrix[r.id] = {};
    for (const p of allPerms[i]) {
      matrix[r.id][p.pageId] = { read: p.read, create: p.create, edit: p.edit, delete: p.delete, whatsapp: p.whatsapp, email: p.email };
    }
  });
  Q.permissions = matrix;

  Q.users = users.map(u => ({
    id: u.id, name: u.name, username: u.username, email: u.email, role: u.role,
    status: u.status, lastSeen: isoToLocal(u.lastSeen),
  }));

  Q.schemes = schemes.map(s => ({
    id: s.id, code: s.code, name: s.name,
    members: s.members, totalAmount: s.totalAmount,
    matured: s.matured, nonMatured: s.nonMatured,
    created: s.created, status: s.status, desc: s.description,
  }));

  Q.events = events.map(e => ({
    id: e.id, schemeId: e.schemeId, name: e.name, date: e.date,
    status: e.status || 'active',
    createdByName: e.createdByName,
    defaultBankId: e.defaultBankId, defaultBankName: e.defaultBankName,
    deposits: e.deposits, withdrawals: e.withdrawals, transactions: e.transactions,
  }));

  Q.transactions = (txsResp.items || []).map(t => ({
    id: t.id, date: isoToLocal(t.date), schemeId: t.schemeId, eventId: t.eventId,
    memberItsNo: t.memberItsNo, member: t.member, type: t.type, amount: t.amount, bank: t.bank,
    commitmentMonths: t.commitmentMonths,
    maturity: t.maturity, operator: t.operatorName, status: t.status, note: t.note,
    documentCount: t.documentCount || 0,
    memberMobile: t.memberMobile || null, memberEmail: t.memberEmail || null,
  }));
  // Server-reported total — independent of the 500-row page cap above.
  // Pages that show "N transactions" should read this rather than
  // Q.transactions.length, which silently underreports once the table
  // exceeds the cap.
  Q.transactionsTotal = txsResp.total ?? Q.transactions.length;
  // Server-reported deposit/withdrawal sums over ALL transactions (not the
  // 500-row page, not per-bank balances) so the Transactions KPI strip
  // includes free-text/blank-bank and inactive-bank rows too.
  Q.transactionsDeposits = txsResp.totalDeposits ?? 0;
  Q.transactionsWithdrawals = txsResp.totalWithdrawals ?? 0;

  Q.financeApps = fin.map(a => ({
    id: a.id, memberItsNo: a.memberItsNo, applicant: a.applicant,
    purpose: a.purpose, purposeOther: a.purposeOther,
    appliedAmount: a.appliedAmount,
    amount: a.amount, tenure: a.tenure, applied: a.applied,
    status: a.status, officer: a.officerName,
    approvedOn: a.approvedOn, rejectedOn: a.rejectedOn, reason: a.reason,
    collateral: a.collateralGold != null
      ? { gold: a.collateralGold, cheques: a.collateralCheques }
      : null,
    documentCount: a.documentCount || 0,
  }));

  Q.repayments = rep.map(r => ({
    id: r.id, appId: r.appId, memberItsNo: r.memberItsNo, applicant: r.applicant,
    dueDate: r.dueDate, amount: r.amount, bank: r.bank,
    cheque: r.cheque, status: r.status,
    clearedOn: r.clearedOn || null,
    rotatedToAppId: r.rotatedToAppId || null,
  }));

  Q.defaulters = def.map(d => ({
    id: d.id, memberItsNo: d.memberItsNo, applicant: d.applicant, appId: d.appId, sinceDate: d.sinceDate,
    dueAmount: d.dueAmount, reason: d.reason,
  }));

  Q.banks = (banks || []).map(b => ({
    id: b.id, name: b.name, code: b.code, branch: b.branch,
    accountNumber: b.accountNumber, notes: b.notes, active: b.active,
    depositsIn: b.depositsIn, withdrawalsOut: b.withdrawalsOut,
    transfersIn: b.transfersIn, transfersOut: b.transfersOut, balance: b.balance,
  }));
  Q.bankTransfers = (bankTransfers || []).map(t => ({
    id: t.id, date: isoToLocal(t.date),
    fromBankId: t.fromBankId, fromBankName: t.fromBankName,
    toBankId: t.toBankId, toBankName: t.toBankName,
    amount: t.amount, reference: t.reference, note: t.note, operator: t.operatorName,
    documentCount: t.documentCount || 0,
  }));

  Q.members = (membersResp.items || []).map(m => ({
    itsNo: m.itsNo, cpr: m.cpr,
    fullName: m.fullName,
    mobile1: m.mobile1, mobile2: m.mobile2, email1: m.email1, email2: m.email2,
    gender: m.gender,
    nominee1Name: m.nominee1Name, nominee1ItsNo: m.nominee1ItsNo, nominee1Mobile: m.nominee1Mobile,
    nominee2Name: m.nominee2Name, nominee2ItsNo: m.nominee2ItsNo, nominee2Mobile: m.nominee2Mobile,
    active: m.active,
  }));
  // Server-reported totals — independent of the loaded page. Reads
  // activeMembersTotal for the sidebar badge so the count stays accurate
  // when member rows exceed the loadAll pageSize cap (1000 today).
  Q.membersTotal = membersResp.total ?? Q.members.length;
  Q.activeMembersTotal = membersResp.totalActive ?? Q.members.filter(m => m.active).length;

  Q.auditLog = (auditResp.items || []).map(a => ({
    ts: isoToLocal(a.timestamp), user: a.userName, role: a.role,
    action: a.action, target: a.target, detail: a.detail,
  }));

  Q.templates = templates.map(t => ({
    id: t.id, name: t.name, channel: t.channel, scope: t.scope, body: t.body,
    // trigger + schemeId are how pickTemplate() in screens-events.jsx
    // resolves which template to render for a given (trigger, scheme).
    // Dropping them here meant pickTemplate never matched, which made
    // the WhatsApp / preview UI silently fall back to the hardcoded
    // default message no matter what the operator edited.
    trigger: t.trigger, schemeId: t.schemeId,
  }));

  // Per-(member, scheme) balance lookup used by the EventDetailPage
  // WhatsApp / preview helpers. Populated by `loadMemberBalances()` on
  // demand — pages that need it call that helper, and `reload()`
  // refreshes it after every mutation. Falls back to an empty object
  // when the role doesn't have members:read so callers can index into
  // it without a null check.
  Q.memberSchemeBalances = Q.memberSchemeBalances || {};

  Q.monthlyDeposits = (dash.cashflow || []).map(c => ({ m: c.m, dep: c.dep, wd: c.wd }));
  Q.dashboardKpis = dash.kpis || {};

  notify();

  // Refresh the (member, scheme) balance lookup in the background. Don't
  // await — every consumer of QH_DATA.memberSchemeBalances only needs it
  // for WhatsApp message rendering, which is initiated by an explicit
  // user click (icon button), not on first paint. Fire-and-forget keeps
  // the bootstrap fast on slow networks.
  loadMemberBalances().catch(() => {});
}

// Refresh QH_DATA.memberSchemeBalances. Cheap: one request that aggregates
// the cross-tab. Called on bootstrap and after any tx mutation that could
// shift balances (deposit / withdrawal / edit).
async function loadMemberBalances() {
  try {
    const r = await api.get('/reports/member-balances');
    const map = {};
    for (const row of r.rows || []) {
      for (const [schemeId, bal] of Object.entries(row.balances || {})) {
        map[`${row.itsNo}_${schemeId}`] = Number(bal);
      }
    }
    window.QH_DATA.memberSchemeBalances = map;
    notify();
  } catch {
    // Role lacks members:read or the network is down. Leave the cache
    // as-is so any prior values keep working; clear only on logout.
  }
}

function can(page, action) {
  const p = (window.QH_DATA.myPerms || {})[page];
  return !!(p && p[action]);
}

window.QH_STORE = {
  loadAll,
  reload: loadAll,
  loadMemberBalances,
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  bump: notify,
  can,
};
window.can = can;
